import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import type { ReportForList } from '@/types/reports';
import { reportsService } from '@/services/reports';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { useToast } from '@/hooks/use-toast';
import { PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { canSendReport } from '@/lib/reportStatus';
import { hasPermission } from '@/utils/permissions';
import { canAccessPartner, getAccessRole, getLinkedPartners, isBrandScopedUser } from '@/utils/rbac';
import { GERAL_VALUE, PARTNER_OPTIONS_DEFAULT } from '../constants';
import type { ReportSort, ReportSortKey } from '../types';
import { nextReportSort, sortReports } from '../utils/sortReports';

const REPORT_SORT_QUERY: Record<ReportSortKey, string> = {
  title: 'name',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  partner_name: 'partner_name',
  visitor_count: 'visitor_count',
  view_count: 'view_count',
  status: 'status',
};

type OpenSendState = {
  reportId: string;
  reportTitle: string;
};

export function useReportsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const linkedPartners = getLinkedPartners(user);
  const linkedPartnersKey = linkedPartners.map((partner) => `${partner.id}:${partner.name}`).join('|');
  const brandScoped = isBrandScopedUser(user);
  const singleLinkedPartnerId = brandScoped && linkedPartners.length === 1 ? linkedPartners[0].id : '';
  const defaultPartnerFilter = singleLinkedPartnerId || GERAL_VALUE;
  const [reports, setReports] = useState<ReportForList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [partnerFilter, setPartnerFilter] = useState<string>(defaultPartnerFilter);
  const [copyingReportId, setCopyingReportId] = useState<string | null>(null);
  const [duplicateModal, setDuplicateModal] = useState<{ reportId: string; reportTitle: string } | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ reportId: string; reportTitle: string } | null>(null);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [sort, setSort] = useState<ReportSort | null>(null);

  const { partners } = useData();
  const { toast } = useToast();

  const partnerOptions = useMemo(() => {
    if (brandScoped && linkedPartners.length > 0) {
      return linkedPartners.map((partner) => ({ value: partner.id, label: partner.name }));
    }
    return [...PARTNER_OPTIONS_DEFAULT, ...partners.map((p) => ({ value: p.id, label: p.name }))];
  }, [brandScoped, linkedPartnersKey, partners]);

  const sortedReports = useMemo(() => sortReports(reports, sort), [reports, sort]);

  const toggleSort = (key: ReportSortKey) => {
    setSort((current) => nextReportSort(current, key));
    setCurrentPage(1);
  };

  useEffect(() => {
    const openSend = (location.state as { openSend?: OpenSendState } | null)?.openSend;
    if (!openSend?.reportId) return;
    navigate(`/app/reports/${openSend.reportId}/view/send`, { replace: true });
  }, [location.state, navigate]);

  useEffect(() => {
    if (!singleLinkedPartnerId) return;
    setPartnerFilter(singleLinkedPartnerId);
  }, [singleLinkedPartnerId]);

  const loadReports = async () => {
    setLoading(true);
    setError(false);
    try {
      const offset = (currentPage - 1) * PAGINATION_PAGE_SIZE;
      const from = dateRange?.from;
      const to = dateRange?.to ?? from;
      const start_date = from ? format(from, 'yyyy-MM-dd') : undefined;
      const end_date = to ? format(to, 'yyyy-MM-dd') : undefined;
      const role = getAccessRole(user);
      if (brandScoped && (role === 'CLIENT' || role === 'GUEST') && linkedPartners.length === 0) {
        setReports([]);
        setTotal(0);
        return;
      }
      const scopedPartnerId =
        partnerFilter && partnerFilter !== GERAL_VALUE
          ? partnerFilter
          : brandScoped && linkedPartners.length === 1
            ? linkedPartners[0].id
            : undefined;
      const response = await reportsService.getAllReports({
        offset,
        limit: PAGINATION_PAGE_SIZE,
        q: searchApplied || undefined,
        partner_id: scopedPartnerId,
        start_date,
        end_date,
        sort_by: sort ? REPORT_SORT_QUERY[sort.key] : undefined,
        order: sort?.direction,
      });
      const visibleReports = brandScoped
        ? response.data.filter((item) => canAccessPartner(user, item.partner_id))
        : response.data;
      setReports(visibleReports);
      setTotal(
        visibleReports.length === response.data.length
          ? response.total
          : Math.max(0, response.total - (response.data.length - visibleReports.length))
      );
    } catch (err) {
      console.error('Erro ao carregar reports:', err);
      setReports([]);
      setTotal(0);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    loadReports();
  }, [currentPage, searchApplied, partnerFilter, dateRange?.from, dateRange?.to, authLoading, sort?.key, sort?.direction]);

  const handlePartnerFilterChange = (value: string) => {
    setPartnerFilter(value);
    setCurrentPage(1);
  };

  const handleApplySearch = () => {
    setSearchApplied(search);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearch('');
    setSearchApplied('');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setSearchApplied('');
    setDateRange(undefined);
    setPartnerFilter(defaultPartnerFilter);
    setCurrentPage(1);
  };

  const openDuplicateModal = (reportId: string, reportTitle: string) => {
    setDuplicateModal({ reportId, reportTitle });
  };

  const closeDuplicateModal = () => {
    setDuplicateModal(null);
  };

  const handleConfirmDuplicate = async (title: string) => {
    if (!duplicateModal) return;
    const { reportId } = duplicateModal;
    setCopyingReportId(reportId);
    try {
      const newReport = await reportsService.duplicateReport(reportId, { title });
      if (newReport) {
        toast({
          title: 'Report duplicado',
          description: `"${newReport.title}" foi criado.`,
        });
        setDuplicateModal(null);
        loadReports();
      } else {
        toast({
          variant: 'error',
          title: 'Erro ao duplicar',
          description: 'Não foi possível duplicar o report.',
        });
      }
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao duplicar',
        description: 'Não foi possível duplicar o report.',
      });
    } finally {
      setCopyingReportId(null);
    }
  };

  const handleCopyReport = (reportId: string) => {
    const report = reports.find((r) => r.id === reportId);
    openDuplicateModal(reportId, report?.title ?? '');
  };

  const openDeleteModal = (reportId: string, reportTitle: string) => {
    setDeleteModal({ reportId, reportTitle });
  };

  const closeDeleteModal = () => {
    setDeleteModal(null);
  };

  const openSendFlow = (reportId: string) => {
    if (!hasPermission(user, 'send_reports')) return;
    const report = reports.find((item) => item.id === reportId);
    if (!report || !canSendReport(report.status)) {
      return;
    }
    navigate(`/app/reports/${reportId}/view/send`);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal) return;
    const { reportId, reportTitle } = deleteModal;
    setDeletingReportId(reportId);
    try {
      const deleted = await reportsService.deleteReport(reportId);
      if (deleted) {
        toast({
          title: 'Report excluído',
          description: `"${reportTitle}" foi removido.`,
        });
        setDeleteModal(null);
        loadReports();
      } else {
        toast({
          variant: 'error',
          title: 'Erro ao excluir',
          description: 'Não foi possível excluir o report.',
        });
      }
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir o report.',
      });
    } finally {
      setDeletingReportId(null);
    }
  };

  return {
    reports: sortedReports,
    sort,
    toggleSort,
    loading,
    error,
    total,
    currentPage,
    setCurrentPage,
    search,
    setSearch,
    handleApplySearch,
    handleClearSearch,
    dateRange,
    setDateRange,
    partnerFilter,
    partnerOptions,
    handlePartnerFilterChange,
    handleClearFilters,
    copyingReportId,
    handleCopyReport,
    loadReports,
    duplicateModal,
    closeDuplicateModal,
    handleConfirmDuplicate,
    deleteModal,
    closeDeleteModal,
    handleConfirmDelete,
    deletingReportId,
    handleDeleteReport: openDeleteModal,
    handleStartSend: openSendFlow,
  };
}
